import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2,
  KeyRound,
  Lock,
  LockOpen,
  Trash2,
  Send,
  Copy,
  Check,
  ShieldOff,
  ShieldCheck,
  Mail,
} from "lucide-react";
import { getToken, formatApiError } from "@/lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ Authorization: `Bearer ${getToken()}` });

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function MembersTab({ onDelete }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [tempPw, setTempPw] = useState(null);  // { id, password }

  const load = async (search = "") => {
    setLoading(true); setError("");
    try {
      const qs = search ? `?q=${encodeURIComponent(search)}` : "";
      const { data } = await axios.get(`${API}/admin/members${qs}`, { headers: authHdr() });
      setData(data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(""); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const resetPw = async (m, notify = true) => {
    if (!window.confirm(`Resetto la password per ${m.email}? ${notify ? "Le verrà inviata via email." : ""}`)) return;
    setBusyId(m.id);
    try {
      const { data } = await axios.post(
        `${API}/admin/members/${m.id}/reset-password`,
        { notify },
        { headers: authHdr() }
      );
      setTempPw({ id: m.id, email: m.email, password: data.temporary_password, sent: data.email_sent });
      load(q);
    } catch (err) { alert(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  const toggleAccess = async (m) => {
    setBusyId(m.id);
    try {
      await axios.post(
        `${API}/admin/members/${m.id}/toggle-access`,
        {},
        { headers: authHdr() }
      );
      load(q);
    } catch (err) { alert(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  const approveMember = async (m) => {
    if (!window.confirm(`Approvo la registrazione di ${m.email}?\nVerrà inviata email con credenziali di accesso.`)) return;
    setBusyId(m.id);
    try {
      const { data } = await axios.post(
        `${API}/admin/members/${m.id}/approve`,
        {},
        { headers: authHdr() }
      );
      setTempPw({ id: m.id, email: m.email, password: data.temporary_password, sent: data.email_sent });
      load(q);
    } catch (err) { alert(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  return (
    <div data-testid="admin-members-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex-1 min-w-[260px] max-w-md">
          <input
            type="text"
            placeholder="Cerca per email, nome..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-members-search"
            className="w-full px-3 py-2 border border-[#0B192C]/20 bg-[#FFFFFF] text-sm focus:outline-none focus:border-[#0B192C]"
          />
        </div>
        <p className="text-xs uppercase tracking-[0.18em] text-[#3A4F6B] whitespace-nowrap">
          {data?.total ?? 0} membri attivi · {data?.total_subscribers ?? 0} iscritti totali
        </p>
      </div>

      {tempPw && (
        <div data-testid="temp-pw-alert" className="mb-4 border border-[#E89B6C] bg-[#F4E8D6] p-4 text-sm font-light text-[#0B192C] flex items-center justify-between gap-4">
          <div>
            <p><strong>Password resettata</strong> per <code className="font-mono">{tempPw.email}</code>.</p>
            <p className="mt-1">Nuova password: <code className="font-mono bg-white px-2 py-0.5 select-all">{tempPw.password}</code>{" "}
              {tempPw.sent ? <span className="text-[#0E7C8F]">· ✓ Inviata via email</span> : <span className="text-red-700">· Invio email fallito (mostra al membro manualmente)</span>}
            </p>
          </div>
          <button onClick={() => setTempPw(null)} className="text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#E89B6C]">Chiudi</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-3 text-[#3A4F6B]"><Loader2 className="animate-spin" size={18} /><span className="text-sm">Caricamento...</span></div>
      ) : error ? (
        <p className="text-sm text-red-700">{error}</p>
      ) : !data?.items?.length ? (
        <div className="border border-dashed border-[#E5E1DA] py-16 text-center text-sm text-[#3A4F6B]">Nessun membro con accesso area iscritti.</div>
      ) : (
        <div className="overflow-x-auto border border-[#E5E1DA]">
          <table className="w-full text-sm" data-testid="admin-members-table">
            <thead className="bg-[#F4E8D6]/60 text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]">
              <tr>
                <th className="text-left px-4 py-3 font-normal">Email</th>
                <th className="text-left px-4 py-3 font-normal">Nome</th>
                <th className="text-left px-4 py-3 font-normal">Iscrizione</th>
                <th className="text-left px-4 py-3 font-normal">Ultimo reset</th>
                <th className="text-left px-4 py-3 font-normal">Stato</th>
                <th className="text-left px-4 py-3 font-normal">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E1DA]">
              {data.items.map((m) => (
                <tr key={m.id} className="hover:bg-[#F9F8F6]" data-testid={`admin-member-${m.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{m.email}</td>
                  <td className="px-4 py-3">{m.name || "—"}</td>
                  <td className="px-4 py-3 text-xs">{fmtDate(m.created_at)}</td>
                  <td className="px-4 py-3 text-xs text-[#3A4F6B]">{fmtDate(m.password_reset_at)}</td>
                  <td className="px-4 py-3">
                    {m.pending_approval ? (
                      <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] bg-[#E89B6C] text-[#0B192C] font-bold">In attesa</span>
                    ) : m.has_access ? (
                      <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] bg-[#0E7C8F] text-[#F9F8F6]">Attivo</span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] bg-red-200 text-red-800">Bloccato</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {m.pending_approval && (
                        <button
                          onClick={() => approveMember(m)}
                          disabled={busyId === m.id}
                          data-testid={`approve-member-${m.id}`}
                          title="Approva registrazione e invia credenziali via email"
                          className="inline-flex items-center gap-1 px-3 py-1 bg-[#0B7C5C] text-white text-[10px] uppercase tracking-[0.18em] hover:bg-[#0B192C] disabled:opacity-60 font-bold"
                        >
                          {busyId === m.id ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
                          Approva
                        </button>
                      )}
                      <button
                        onClick={() => resetPw(m, true)}
                        disabled={busyId === m.id}
                        data-testid={`reset-pw-${m.id}`}
                        title="Genera nuova password e inviala via email"
                        className="inline-flex items-center gap-1 px-3 py-1 border border-[#0E7C8F] text-[#0E7C8F] text-[10px] uppercase tracking-[0.18em] hover:bg-[#0E7C8F] hover:text-[#F9F8F6] disabled:opacity-60"
                      >
                        {busyId === m.id ? <Loader2 size={11} className="animate-spin" /> : <KeyRound size={11} />}
                        Reset PW
                      </button>
                      <button
                        onClick={() => toggleAccess(m)}
                        disabled={busyId === m.id}
                        data-testid={`toggle-access-${m.id}`}
                        title={m.has_access ? "Blocca accesso" : "Sblocca accesso"}
                        className={`inline-flex items-center gap-1 px-3 py-1 border text-[10px] uppercase tracking-[0.18em] disabled:opacity-60 ${
                          m.has_access ? "border-red-700 text-red-700 hover:bg-red-700 hover:text-white" : "border-[#0E7C8F] text-[#0E7C8F] hover:bg-[#0E7C8F] hover:text-white"
                        }`}
                      >
                        {m.has_access ? <><ShieldOff size={11} /> Blocca</> : <><ShieldCheck size={11} /> Sblocca</>}
                      </button>
                      <button
                        onClick={() => onDelete(m.id)}
                        title="Elimina iscritto (rimuove anche dal DB)"
                        data-testid={`delete-member-${m.id}`}
                        className="text-red-700 hover:text-red-900"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
